import { MOTION, SHADOW } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { ArticleCover } from "~/components/news/ArticleCover";
import { FormatBadge } from "~/components/news/FormatBadge";
import { SaveButton } from "~/components/news/SaveButton";
import { useLocale } from "~/i18n";
import { topicLabel } from "~/lib/taxonomy";
import { useImpression } from "~/lib/use-impression";
import { useAuthStore } from "~/store/auth-store";
import type { NewsArticleSummary } from "~/types/news";

/**
 * One item in the feed. The whole card is the link target, not just the title.
 *
 * `featured` marks the lead article. On desktop it becomes a wide two-up — artwork on
 * the left, copy on the right. On a phone it stays a single column but keeps the larger
 * body size and the longer excerpt clamp, so the lead still reads as the lead.
 *
 * `showTopics` turns the card into a protocols tile. The topics are deliberately
 * inert there: the whole card is already a link, and a button inside a link is both
 * invalid markup and an unpredictable tap target on a phone. Filtering is the
 * selector's job.
 *
 * The format badge is on every card, not just the library's. A member tapping a
 * video from the dashboard should know it is a video before the page loads, and the
 * feed carries four kinds of thing now — the date and title alone no longer say
 * which one this is.
 *
 * The heart sits top-right of the artwork, *beside* the link rather than inside it:
 * a button in a link is invalid markup and an unpredictable tap target on a phone.
 */
export function NewsCard({
  article,
  featured = false,
  showTopics = false,
  onImpression,
  onSelect,
}: {
  article: NewsArticleSummary;
  featured?: boolean;
  showTopics?: boolean;
  /** Fired once when the card first reaches the viewport. */
  onImpression?: () => void;
  /** Fired when the card is opened, before the router navigates. */
  onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const userId = useAuthStore((state) => state.user?.id);
  // The observer attaches to the card's own root, so instrumenting the feed needs no
  // wrapper element and cannot disturb the grid.
  const impressionRef = useImpression<HTMLDivElement>(onImpression);

  return (
    <Card
      ref={impressionRef}
      sx={{
        position: "relative",
        height: "100%",
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        // The resting lift comes from the theme; hover has to go further than that to
        // read as a change now that every card already floats.
        "&:hover": { boxShadow: SHADOW.raised, transform: "translateY(-2px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/news/${article.slug}`}
        data-testid={`news.card-${article.slug}`}
        onClick={onSelect}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: featured ? { xs: "column", md: "row" } : "column",
          alignItems: "stretch",
          // Without this the action area centres its children and the cards in a row
          // stop lining up.
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            // The badge is absolutely positioned against this, not against the card,
            // so it stays on the artwork when a featured card puts the cover beside
            // the copy instead of above it.
            width: featured ? { xs: "100%", md: "48%" } : "100%",
            // Beside the copy, the 16:9 cover is centred against however tall the
            // text runs rather than stretched (and so cropped) to match it.
            alignSelf: featured ? { md: "center" } : undefined,
          }}
        >
          <ArticleCover
            slug={article.slug}
            src={article.cover_image_url}
            alt=""
            sx={{ borderRadius: 0 }}
          />
          <FormatBadge format={article.format} />
          {/* Reserves the corner the heart (a sibling of the link, below) occupies. */}
        </Box>

        <Box
          sx={{
            padding: { xs: 2.5, md: featured ? 4 : 3 },
            display: "flex",
            flexDirection: "column",
            gap: 1,
            justifyContent: featured ? "center" : "flex-start",
            flex: 1,
          }}
        >
          <Typography
            variant="L3"
            sx={{
              color: "text.muted",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            <time dateTime={article.published_at}>
              {formatDate(article.published_at)}
            </time>
          </Typography>

          <Typography variant="H3" sx={{ color: "text.primary" }}>
            {article.title}
          </Typography>

          {article.excerpt ? (
            <Typography
              variant={featured ? "T1" : "T2"}
              sx={{
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: featured ? 4 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {article.excerpt}
            </Typography>
          ) : null}

          {showTopics && article.topics.length > 0 ? (
            <Box
              component="ul"
              data-testid={`news.card-topics-${article.slug}`}
              sx={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                // Pushed to the foot of the card so a row of tiles lines its topics
                // up however unevenly the excerpts above them wrap.
                marginTop: "auto",
                paddingTop: 1.5,
              }}
            >
              {article.topics.map((topic) => (
                <Box component="li" key={topic}>
                  <Chip
                    label={topicLabel(t, topic)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      </CardActionArea>
      {userId ? (
        <SaveButton
          slug={article.slug}
          userId={userId}
          articleId={article.id}
          testId={`news.save-${article.slug}`}
          sx={{ position: "absolute", top: 8, insetInlineEnd: 8 }}
        />
      ) : null}
    </Card>
  );
}
